import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Send, Loader2, Headphones, Image as ImageIcon, X, HelpCircle, Copy, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation, useLanguage } from '../context/LanguageContext';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import { toast } from 'sonner';
import AuthImage from '../components/AuthImage';
import { collectDiagnostics, formatDiagnostics } from '../lib/supportDiagnostics';

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '';
  }
}

export default function Assistenza() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pickedImage, setPickedImage] = useState(null); // base64 data URL
  const [pickedName, setPickedName] = useState('');
  const [lightbox, setLightbox] = useState(null);       // {url, isObjectUrl}
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error(t('support.imageTooLarge', 'Immagine troppo grande (max 4MB)'));
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPickedImage(reader.result);
      setPickedName(file.name);
    };
    reader.onerror = () => toast.error(t('support.imageError', 'Errore lettura immagine'));
    reader.readAsDataURL(file);
  };

  const openLightbox = async (msgId) => {
    try {
      const token = localStorage.getItem('session_token') || localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/support/messages/${msgId}/image`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setLightbox({ url, isObjectUrl: true });
    } catch (err) {
      toast.error(t('support.imageLoadError', 'Errore caricamento immagine'));
    }
  };

  const closeLightbox = () => {
    if (lightbox?.isObjectUrl) URL.revokeObjectURL(lightbox.url);
    setLightbox(null);
  };

  // Initial fetch + mark as read when the page opens
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await httpClient.get(`${API_URL}/api/support/chat`);
        if (cancelled) return;
        setMessages(data?.messages || []);
        // Mark all admin → user as read in the background
        httpClient.post(`${API_URL}/api/support/chat/read`).catch(() => {});
      } catch (err) {
        toast.error(t('support.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [t]);

  // Light polling so an admin reply lands in <10s without manual refresh
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await httpClient.get(`${API_URL}/api/support/chat`);
        const next = data?.messages || [];
        setMessages((prev) => (next.length !== prev.length ? next : prev));
        if (next.length > messages.length) {
          httpClient.post(`${API_URL}/api/support/chat/read`).catch(() => {});
        }
      } catch (_) { /* silent */ }
    }, 8000);
    return () => clearInterval(id);
  }, [messages.length]);

  // Autoscroll on new message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async () => {
    const trimmed = text.trim();
    if ((!trimmed && !pickedImage) || sending) return;
    setSending(true);
    // Optimistic
    const optimistic = {
      msg_id: `tmp_${Date.now()}`,
      from: 'user',
      text: trimmed,
      _localImage: pickedImage,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setText('');
    const imgPayload = pickedImage;
    setPickedImage(null);
    setPickedName('');
    if (fileRef.current) fileRef.current.value = '';
    try {
      const { data } = await httpClient.post(`${API_URL}/api/support/chat`, {
        text: trimmed,
        image_base64: imgPayload || undefined,
        language,
      });
      setMessages((prev) => prev.map((m) => (m.msg_id === optimistic.msg_id ? data.message : m)));
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.msg_id !== optimistic.msg_id));
      toast.error(err.response?.data?.detail || t('support.sendError'));
      setText(trimmed);
      setPickedImage(imgPayload);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`${isDark ? 'bg-[#0D1424]' : 'bg-white'} min-h-screen`} data-testid="assistenza-page">
      {/* Fixed header */}
      <div className={`fixed-header ${isDark ? 'bg-[#0D1424]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-md mx-auto flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className={`p-1.5 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            data-testid="assistenza-back"
          >
            <ChevronLeft className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-900'}`} />
          </button>
          <Headphones className="w-5 h-5 text-[#E8334A]" />
          <div className="flex-1">
            <h1 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('support.title')}
            </h1>
            <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('support.chatSubtitle')}
            </p>
          </div>
          <DiagnosticsButton
            isDark={isDark}
            onInsertIntoComposer={(diagText) => {
              // Direct-insert path: bypasses the iOS WKWebView clipboard
              // bug where pasted text gets URL-encoded (`%20`, `%0A`,
              // `%C3%AC`) inside the textarea. Inserts the diagnostics
              // block straight into the composer, prepending it to any
              // text the user has already typed (so we never destroy
              // their in-progress message).
              setText((prev) => {
                const sep = prev && !prev.endsWith('\n') ? '\n\n' : '';
                const next = (prev || '') + sep + diagText;
                return next.slice(0, 2000);
              });
            }}
          />
        </div>
      </div>

      {/* Content (scrolls behind input) */}
      <div
        className="px-4"
        style={{
          paddingTop: 'calc(72px + env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(180px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#E8334A] animate-spin" />
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className={`mt-12 text-center ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            <Headphones className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
            <p className="font-medium">{t('support.emptyTitle')}</p>
            <p className="text-sm mt-2">{t('support.emptyHint')}</p>
          </div>
        )}

        <div className="space-y-2 pt-4">
          {messages.map((m) => {
            const mine = m.from === 'user';
            return (
              <div
                key={m.msg_id}
                className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-msg-${m.from}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                    mine
                      ? 'bg-[#E8334A] text-white'
                      : isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'
                  } ${m._pending ? 'opacity-60' : ''}`}
                >
                  {!mine && (
                    <div className={`text-[11px] font-semibold mb-0.5 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      {m.admin_name || 'Slimmy'}
                    </div>
                  )}
                  {m.related_bug_id && m.related_bug_quote && (
                    <div className={`mb-1.5 px-2 py-1 rounded-lg text-[11px] border-l-2 border-[#E8334A] ${
                      isDark ? 'bg-white/5 text-white/60' : 'bg-black/5 text-gray-600'
                    }`}>
                      <div className="font-semibold mb-0.5">
                        {t('support.replyToBug', 'Risposta al tuo bug:')}
                      </div>
                      <div className="italic line-clamp-2">{m.related_bug_quote}</div>
                    </div>
                  )}
                  {(m.image_path || m._localImage) && (
                    <button
                      onClick={() => m.image_path && openLightbox(m.msg_id)}
                      disabled={!m.image_path}
                      className="block mb-1.5 rounded-xl overflow-hidden bg-black/10"
                      data-testid={`chat-img-${m.msg_id}`}
                    >
                      {m._localImage ? (
                        <img
                          src={m._localImage}
                          alt=""
                          className="max-w-[220px] max-h-[220px] object-cover"
                        />
                      ) : (
                        <AuthImage
                          src={`${API_URL}/api/support/messages/${m.msg_id}/image`}
                          alt=""
                          className="max-w-[220px] max-h-[220px] object-cover cursor-zoom-in"
                        />
                      )}
                    </button>
                  )}
                  {m.text && (
                    <div className="whitespace-pre-wrap break-words text-sm">{m.text}</div>
                  )}
                  <div className={`text-[10px] mt-1 ${mine ? 'text-white/70' : (isDark ? 'text-white/40' : 'text-gray-400')}`}>
                    {formatTime(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Composer (fixed at bottom, above safe-area) */}
      <div
        className={`fixed bottom-0 left-0 right-0 ${isDark ? 'bg-[#0D1424]' : 'bg-white'} border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}
        style={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-md mx-auto px-3 pt-2">
          {/* Image preview pill (when an image has been picked but not yet sent) */}
          {pickedImage && (
            <div className={`mb-2 flex items-center gap-2 p-2 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <img src={pickedImage} alt="" className="w-10 h-10 rounded object-cover" />
              <span className={`flex-1 text-xs truncate ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{pickedName}</span>
              <button
                onClick={() => { setPickedImage(null); setPickedName(''); if (fileRef.current) fileRef.current.value = ''; }}
                className={`p-1 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                data-testid="chat-image-remove"
              >
                <X className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
              </button>
            </div>
          )}

          <div className="flex items-end gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFile}
              className="hidden"
              data-testid="chat-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={sending}
              className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-50 ${
                isDark ? 'bg-white/5 text-white/70 hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="chat-attach-btn"
              aria-label={t('support.attachImage', 'Allega immagine')}
            >
              <ImageIcon className="w-5 h-5" />
            </button>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 2000))}
              placeholder={t('support.composerPlaceholder')}
              rows={3}
              data-testid="chat-composer"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              className={`flex-1 max-h-48 p-3 rounded-2xl outline-none resize-none ${
                isDark
                  ? 'bg-white/5 text-white placeholder:text-white/30 border border-white/10'
                  : 'bg-gray-100 text-gray-900 placeholder:text-gray-400'
              }`}
            />
            <button
              onClick={send}
              disabled={sending || (!text.trim() && !pickedImage)}
              className="w-12 h-12 rounded-full bg-[#E8334A] text-white flex items-center justify-center disabled:opacity-50 flex-shrink-0"
              data-testid="chat-send"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Image lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/95 z-[10000] flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="chat-lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <img
            src={lightbox.url}
            alt=""
            className="max-w-full max-h-full rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Round "?" button in the Assistenza header. Opens a popup showing
 * build info + native bridge state + UA + a Copy button to paste
 * into the support chat.
 */
function DiagnosticsButton({ isDark, onInsertIntoComposer }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const info = open ? collectDiagnostics() : null;

  const doInsert = () => {
    if (!info) return;
    const text = formatDiagnostics(info);
    onInsertIntoComposer?.(text);
    setOpen(false);
  };

  const doCopy = async () => {
    if (!info) return;
    const text = formatDiagnostics(info);
    try {
      // 1) Native (iOS/Android): use Capacitor Clipboard plugin so the
      //    text is written directly to UIPasteboard/ClipboardManager.
      //    The plain `navigator.clipboard.writeText` path in iOS WKWebView
      //    URL-encodes non-ASCII + special chars (the original bug).
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor?.isNativePlatform?.()) {
          const { Clipboard } = await import('@capacitor/clipboard');
          await Clipboard.write({ string: text });
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
          return;
        }
      } catch (_) { /* fall through to web path */ }

      // 2) Web: standard async clipboard API.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.error('clipboard copy failed', e);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`w-8 h-8 flex items-center justify-center rounded-full border ${
          isDark ? 'border-white/15 text-white/70 hover:bg-white/5' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
        } transition-colors`}
        data-testid="diagnostics-open-btn"
        aria-label="App info"
        title="App info"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {open && info && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="diagnostics-modal"
        >
          <div
            className={`${isDark ? 'bg-[#0F1424] border border-white/10' : 'bg-white border border-gray-200'} rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>App info</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={`p-1.5 rounded-md ${isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                data-testid="diagnostics-close-btn"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className={`px-5 py-4 space-y-2 text-xs font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {Object.entries(info).map(([k, v]) => (
                <div key={k} className="break-all">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{k}:</span>{' '}
                  <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{String(v)}</span>
                </div>
              ))}
            </div>
            <div className={`px-5 py-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} space-y-2`}>
              {onInsertIntoComposer && (
                <button
                  type="button"
                  onClick={doInsert}
                  data-testid="diagnostics-insert-btn"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#E8334A] text-white text-sm font-medium hover:bg-[#d12d42] transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Inserisci nella chat
                </button>
              )}
              <button
                type="button"
                onClick={doCopy}
                data-testid="diagnostics-copy-btn"
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors border ${
                  isDark
                    ? 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copiato!' : 'Copia info'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
